import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { metrics__get_category_timeblock_stats } from 'common/src/api/metrics';

interface CategoryTimeblockStats {
  category: string;
  [key: string]: number | string;
}

const CategoryPerTimeblockReport: React.FC = () => {
  const [categoryStats, setCategoryStats] = useState<CategoryTimeblockStats[]>(
    [],
  );
  const [startDate, setStartDate] = useState(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(
    () => new Date().toISOString().split('T')[0],
  );
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const fetchCategoryTimeblockStats = async (start: string, end: string) => {
    const { data, error } = await metrics__get_category_timeblock_stats(start, end)

    if (error) {
      console.error('Error fetching category timeblock stats:', error);
      return [];
    }

    return data;
  };

  useEffect(() => {
    fetchCategoryTimeblockStats(startDate, endDate).then(setCategoryStats);
  }, [startDate, endDate]);

  const timeblocks = Array.from(
    { length: 24 },
    (_, i) => `${i.toString().padStart(2, '0')}:00`,
  );

  const statsWithTotals = useMemo(() => {
    if (categoryStats.length === 0) return [];

    const total = timeblocks.reduce((acc, timeblock) => {
      acc[timeblock] = categoryStats.reduce(
        (sum, stat) => sum + (stat[timeblock] as number),
        0,
      );
      return acc;
    }, {} as CategoryTimeblockStats);
    total.category = 'TOTAL';

    const totalSum = timeblocks.reduce(
      (sum, timeblock) => sum + (total[timeblock] as number),
      0,
    );

    const totalPercentage = timeblocks.reduce((acc, timeblock) => {
      acc[timeblock] = ((total[timeblock] as number) / totalSum) * 100;
      return acc;
    }, {} as CategoryTimeblockStats);
    totalPercentage.category = 'TOTAL %';

    return [...categoryStats, total, totalPercentage];
  }, [categoryStats, timeblocks]);

  const getHeatmapColor = (value: number, max: number) => {
    const intensity = value / max;
    return `rgba(255, 0, 0, ${intensity.toFixed(2)})`;
  };

  const maxValue = useMemo(() => {
    if (categoryStats.length === 0) return 0;
    return Math.max(
      ...categoryStats.flatMap((stat) =>
        timeblocks.map((timeblock) =>
          typeof stat[timeblock] === 'number' ? (stat[timeblock] as number) : 0,
        ),
      ),
    );
  }, [categoryStats, timeblocks]);

  const handleDateSubmit = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(statsWithTotals);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CategoryTimeblockStats');
    XLSX.writeFile(wb, `category_timeblock_stats_${startDate}_${endDate}.xlsx`);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between p-4 bg-white border-b shadow-sm">
        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">
            Seleccionar Rango de Fechas
          </h3>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Inicio
              </label>
              <input
                id="startDate"
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <div>
              <label
                htmlFor="endDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Fin
              </label>
              <input
                id="endDate"
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <button
              onClick={handleDateSubmit}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Aplicar Rango
            </button>
          </div>
        </div>

        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">Otras Opciones</h3>
          <button
            onClick={exportToExcel}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Exportar a Excel
          </button>
        </div>
      </div>

      <div className="flex-grow overflow-auto">
        <div className="inline-block min-w-full">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `minmax(200px, auto) repeat(${timeblocks.length}, minmax(80px, auto))`,
              gridTemplateRows: 'auto',
              width: `calc(200px + ${timeblocks.length * 80}px + 1px)`, // Add 1px for border
            }}
          >
            {/* Header */}
            <div className="sticky top-0 left-0 z-30 bg-gray-100 font-semibold p-2 border-b border-r border-gray-300">
              Categoría
            </div>
            {timeblocks.map((timeblock) => (
              <div
                key={timeblock}
                className="sticky top-0 z-20 bg-gray-100 font-semibold p-2 text-center border-b border-r border-gray-300"
              >
                {timeblock}
              </div>
            ))}

            {/* Data rows */}
            {statsWithTotals.map((stat) => (
              <React.Fragment key={stat.category}>
                <div className="sticky left-0 z-10 bg-white font-medium p-2 border-b border-r border-gray-300">
                  {stat.category}
                </div>
                {timeblocks.map((timeblock) => (
                  <div
                    key={timeblock}
                    className="p-2 border-b border-r border-gray-300"
                    style={{
                      backgroundColor:
                        stat.category !== 'TOTAL' && stat.category !== 'TOTAL %'
                          ? getHeatmapColor(stat[timeblock] as number, maxValue)
                          : 'transparent',
                    }}
                  >
                    {stat.category === 'TOTAL %'
                      ? `${(stat[timeblock] as number).toFixed(2)}%`
                      : stat[timeblock]}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPerTimeblockReport;
